/*

Based off detect-browser 1.6.2 which has a MIT License

MIT License

Copyright (c) 2016 Damon Oehlman

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

import { logger } from '../hlsjs/utils/logger';

function detectBrowserM(userAgentString) {
	let browsers = [
	['edge', /Edge\/([0-9._]+)/],
	['yandexbrowser', /YaBrowser\/([0-9._]+)/],
	['tizen', /Tizen\s([0-9._]+)/],
	['webos3', /Web0S.*Chrome\/([0-9.]+)/],
	['webos2', /Web0S.*Safari\/([0-9.]+)/],
	['chrome', /(?!Chrom.*OPR)Chrom(?:e|ium)\/([0-9.]+)(:?\s|$)/],
	['crios', /CriOS\/([0-9.]+)(:?\s|$)/],
	['firefox', /Firefox\/([0-9.]+)(?:\s|$)/],
	['opera', /Opera\/([0-9.]+)(?:\s|$)/],
	['opera', /OPR\/([0-9.]+)(:?\s|$)$/],
	['ie', /Trident\/7\.0.*rv:([0-9.]+)\).*Gecko$/],
	['ie', /MSIE\s([0-9.]+);.*Trident\/[4-7].0/],
	['ie', /MSIE\s(7\.0)/],
	['bb10', /BB10;\sTouch.*Version\/([0-9.]+)/],
	['android', /Android\s([0-9.]+)/],
	['phantom', /PhantomJS\/([0-9._]+)/],
	['ios', /Version\/([0-9._]+).*Mobile.*Safari.*/],
	['safari', /Version\/([0-9._]+).*Safari/],
	['electron', /Electron\s([0-9._]+)/],
	];

	return browsers.map(function (rule) {
		if (rule[1].test(userAgentString)) {
			var match = rule[1].exec(userAgentString);
			var version = match && match[1].split(/[._]/).slice(0, 3);

			if (version && version.length < 3) {
				Array.prototype.push.apply(version, (version.length === 1) ? [0, 0] : [0]);
			}
			return {
				name: rule[0],
				version: version.join('.'),
			};
		}
		else {
			return undefined; //This function relies on the undefined to cause a map shift
		}
	}).filter(Boolean).shift();
}


function detectPlatformM(userAgentString) {
	let platforms = [
	['windows', /Windows\sNT\s([0-9.]+)/],
	];

	return platforms.map(function (rule) {
		if (rule[1].test(userAgentString)) {
			var match = rule[1].exec(userAgentString);
			var version = match && match[1].split(/[._]/).slice(0, 3);

			if (version && version.length < 3) {
				Array.prototype.push.apply(version, (version.length === 1) ? [0, 0] : [0]);
			}
			return {
				name: rule[0],
				version: version.join('.'),
			};
		}
		else {
			return undefined; //This function relies on the undefined to cause a map shift
		}
	}).filter(Boolean).shift();
}


function isNode() {
	try {
		return process.release.name === 'node';
	}
	catch(e) {
		return false;
	}
}


function detectPlatform(userAgentString) {
	var platform = 'unknown';

	if (userAgentString.indexOf('Macintosh') !== -1) {
		platform = 'macintosh';
	}
	else if (userAgentString.indexOf('Windows NT') !== -1) {
		platform = 'windows';
	}
	else if (userAgentString.indexOf('Android') !== -1) {
		platform = 'android';  //Needs to be before Linux
	}
	else if (userAgentString.indexOf('Tizen') !== -1) {
		platform = 'tizen';  //Needs to be before Linux
	}
	else if (userAgentString.indexOf('Web0S') !== -1) {
		platform = 'webos';  //Needs to be before Linux
	}
	else if (userAgentString.indexOf('Linux') !== -1) {
		platform = 'linux';
	}
	else if (userAgentString.indexOf('iPhone') !== -1) {
		platform = 'ios';
	}
	else if (userAgentString.indexOf('iPad') !== -1) {
		platform = 'ios';
	}

	return platform;
}


function detectPlatformVersion(platform, userAgentString) {

	let platformVersion = 'unknown';

	if (platform === 'windows') {
		var spot = userAgentString.indexOf('Windows NT');

		if (spot !== -1) {
			var platformV2 = detectPlatformM(userAgentString);

			if (platformV2 !== undefined) {
				platformVersion = platformV2.version;
			}
			else {
				platformVersion = 'winxx';
			}

		}
		else {
			platformVersion = 'winxx';
		}
	}

	return platformVersion;
}


function detectNodePlatform(platformString) {
	var platform = 'unknown';

	if (platformString.indexOf('darwin') !== -1) {
		platform = 'macintosh';
	}
	else if (platformString.indexOf('win32') !== -1) {
		platform = 'windows';
	}
	else {
		logger.log(`DetectBrowser: Unknown Node platform: {${platformString}}`);
		platform = platformString;
	}

	return platform;
}


function detectBrowser(userAgentString) {
	let usrAgent = userAgentString;
	let detection = {};

	if (!usrAgent && typeof navigator !== 'undefined') {
		usrAgent = navigator.userAgent;
	}

	if (usrAgent) {
		detection = detectBrowserM(usrAgent);
		if (detection !== undefined) {
			if (detection.name === 'webos2') {
				detection.name = 'webos';
				//Need some real world examples here, but it doesn't matter too much...
				if (detection.version === '537.41.0') {
					detection.version = '1.0';
				}
				else if (detection.version === '538.2.0') {
					detection.version = '2.0';
				}
				else { //assume 2.0 otherwise
					detection.version = '2.0';
				}
			}
			else if (detection.name === 'webos3') {
				detection.name = 'webos';
				//Need some real world examples here, but it doesn't matter too much...
				if (detection.version === '38.0.2125') {
					detection.version = '3.0';
				}
				else if (detection.version === '53.0.2785') {
					detection.version = '4.0';
				}
				else { //assume 3.0 otherwise
					detection.version = '3.0';
				}
			}
			detection.platform = detectPlatform(usrAgent);
			detection.platformVersion = detectPlatformVersion(detection.platform, usrAgent);
		}
		else {
			logger.log(`DetectBrowser: Unknown userAgent: {${usrAgent}}`);
		}
	}
	else if(isNode()) {
		detection.name = 'node';
		detection.version = process.version.slice(1);
		detection.platform = detectNodePlatform(process.platform);
		detection.platformVersion = 'unknown';
	}

	if (detection === undefined || detection.version === undefined) {
		detection = {};
		detection.name = 'unknown';
		detection.version = '1.0';
		detection.platform = 'unknown';
		detection.platformVersion = 'unknown';
	}

	return detection;
}

let detectedBrowser = detectBrowser();

export default detectedBrowser;
export { detectedBrowser, detectBrowser };



// WEBPACK FOOTER //
// ./src/utils/detectBrowser.js